// src/AuthSanityTest.js
import React, { useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const LS_USER_KEY = "agr_user_v1";
const LS_USERS_KEY = "agr_users_v1";
const LS_INVITES_KEY = "agr_invites_v1";

function setInvite(email, role) {
  const inv = JSON.parse(localStorage.getItem(LS_INVITES_KEY) || "{}");
  inv[String(email).toLowerCase()] = role;
  localStorage.setItem(LS_INVITES_KEY, JSON.stringify(inv));
}

function clearAll() {
  localStorage.removeItem(LS_USER_KEY);
  localStorage.removeItem(LS_USERS_KEY);
  localStorage.removeItem(LS_INVITES_KEY);
}

export default function AuthSanityTest() {
  const { register, login, logout, user } = useAuth();
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  // deterministic test users (unique by timestamp)
  const ids = useMemo(() => {
    const ts = Date.now();
    return {
      farmer:  { email: `farmer+${ts}@example.com`,  pass: "pass1234", role: "Farmer" },
      partner: { email: `partner+${ts}@example.com`, pass: "pass1234", role: "Partner" },
      admin:   { email: `admin+${ts}@example.com`,   pass: "pass1234", role: "Admin" },
    };
  }, []);

  const append = (msg, ok = true) =>
    setLog((L) => [...L, { t: new Date().toLocaleTimeString(), ok, msg }]);

  async function step0_reset() {
    clearAll();
    append("Reset localStorage (users, invites, session).");
  }

  async function step1_farmer_register_login() {
    const { email, pass, role } = ids.farmer;
    append(`Register Farmer: ${email}`);
    const r = await register(email, pass, role);
    if (!r.ok) return append(`FAILED: ${r.error}`, false);
    append("Farmer registered.");

    append("Login Farmerâ€¦");
    const L = await login(email, pass, role);
    if (!L.ok) return append(`FAILED login: ${L.error}`, false);
    append("Farmer logged in. Visit /dashboard to confirm.");
  }

  async function step2_partner_without_invite_should_fail() {
    const { email, pass, role } = ids.partner;
    append(`Try Partner register WITHOUT invite: ${email}`);
    const r = await register(email, pass, role);
    if (r.ok) return append("FAILED: Partner registration should be blocked (invite-only).", false);
    append(`Correctly blocked: ${r.error}`);
  }

  async function step3_invite_partner_then_register() {
    const { email, pass, role } = ids.partner;
    append(`Invite Partner: ${email}`);
    setInvite(email, role);
    append("Invite stored.");
    append("Register Partner WITH inviteâ€¦");
    const r = await register(email, pass, role);
    if (!r.ok) return append(`FAILED: ${r.error}`, false);
    append("Partner registered (invited).");
  }

  async function step4_login_admin_and_check_role() {
    const { email, pass, role } = ids.admin;
    setInvite(email, role); // enforce invite for Admin too
    append(`Login Admin: ${email}`);
    const L = await login(email, pass, role);
    if (!L.ok) return append(`FAILED: ${L.error}`, false);
    append("Admin logged in. Visit /dashboard to see admin panels.");
  }

  async function step5_check_persistence() {
    const raw = localStorage.getItem(LS_USER_KEY);
    if (!raw) return append("FAILED: No session in localStorage; persistence missing.", false);
    try {
      const obj = JSON.parse(raw);
      append(`Session persisted: ${obj.email} (${obj.role}). Refresh keeps you logged in.`);
    } catch {
      append("FAILED: Could not parse session JSON.", false);
    }
  }

  async function runAll() {
    if (busy) return;
    setBusy(true);
    setLog([]);
    try {
      await step0_reset();
      await step1_farmer_register_login();
      await step2_partner_without_invite_should_fail();
      await step3_invite_partner_then_register();
      await step4_login_admin_and_check_role();
      await step5_check_persistence();
      append("âœ… All sanity checks executed. Open /dashboard to visually confirm.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
      <h2>Auth Sanity Test</h2>
      <p style={{ color: "#bcd6c8" }}>
        Runs the launch checklist: Farmer self-register â†’ login, Partner blocked until invited,
        invite then register, Admin login, and session persistence. Uses temporary test emails.
      </p>

      <div className="card" style={{ marginBottom: 12, display: "grid", gap: 8 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={runAll} disabled={busy}>{busy ? "Runningâ€¦" : "Run All Checks"}</button>
          <button onClick={step0_reset} disabled={busy}>Reset storage</button>
          <button onClick={step1_farmer_register_login} disabled={busy}>1) Farmer register + login</button>
          <button onClick={step2_partner_without_invite_should_fail} disabled={busy}>2) Partner w/o invite (expect fail)</button>
          <button onClick={step3_invite_partner_then_register} disabled={busy}>3) Invite â†’ Partner register</button>
          <button onClick={step4_login_admin_and_check_role} disabled={busy}>4) Admin login</button>
          <button onClick={step5_check_persistence} disabled={busy}>5) Check persistence</button>
          <button onClick={logout} disabled={busy || !user}>Logout current</button>
        </div>

        <div style={{ fontSize: 13, color: "#bcd6c8" }}>
          <div><b>Farmer:</b> {ids.farmer.email}</div>
          <div><b>Partner:</b> {ids.partner.email}</div>
          <div><b>Admin:</b> {ids.admin.email}</div>
          <div><b>Password (all):</b> pass1234</div>
        </div>
      </div>

      <div className="card" style={{ maxHeight: 360, overflow: "auto" }}>
        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }}>
          {log.map((l, i) => (
            <div key={i} style={{ color: l.ok ? "#a7f3d0" : "#ffb4b4" }}>
              [{l.t}] {l.msg}
            </div>
          ))}
          {log.length === 0 && <div style={{ color: "#bcd6c8" }}>Run a step to see logsâ€¦</div>}
        </div>
      </div>
    </div>
  );
}